import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { Platform } from 'react-native'
import * as WebBrowser from 'expo-web-browser'
import * as Linking from 'expo-linking'
import { storage, TOKEN_KEY, USER_ID_KEY, HAS_LOGGED_IN_KEY } from '../utils/storage'
import { API_URL, setOnUnauthorized } from '../api/client'
import axios from 'axios'
import type { AuthUser } from '../types'
import { isBiometricsEnabled, authenticateWithBiometrics, setBiometricsEnabled } from './biometrics'
import { BIOMETRIC_PROMPTED_KEY } from '../utils/storage'
import { identifyUser, resetUser, events } from '../lib/posthog'
import { clearSentryUser } from '../lib/sentry'
import { clearPushToken } from '../notifications'

interface AuthContextType {
  user: AuthUser | undefined
  isAuthenticated: boolean
  isLoading: boolean
  loginWithRedirect: (leadId?: string, trackingCode?: string) => Promise<void>
  logout: () => void
  getAccessTokenSilently: () => Promise<string>
  refreshUser: () => Promise<void>
  setUserDirectly: (user: AuthUser) => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  // Register 401 handler
  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(undefined)
    })
  }, [])

  // On mount: validate stored token (with optional biometric gate)
  useEffect(() => {
    ;(async () => {
      const token = await storage.get(TOKEN_KEY)
      if (!token) {
        setIsLoading(false)
        return
      }

      // Biometric gate (native only) — if enabled, require Face ID / Touch ID
      if (Platform.OS !== 'web') {
        const bioEnabled = await isBiometricsEnabled()
        if (bioEnabled) {
          const passed = await authenticateWithBiometrics()
          if (!passed) {
            // Clear token AND biometrics flag so next launch goes to MitID login (no loop)
            await storage.remove(TOKEN_KEY)
            await storage.remove(USER_ID_KEY)
            await setBiometricsEnabled(false)
            setIsLoading(false)
            return
          }
        }
      }

      try {
        const headers = { Authorization: `Bearer ${token}` }
        const res = await axios.get(`${API_URL}/api/v1/auth/me`, { headers, timeout: 8000 })
        setUser(res.data)
        await storage.set(USER_ID_KEY, res.data.id)
        await storage.set(HAS_LOGGED_IN_KEY, '1')
        // PostHog identify — matches Next.js MitIDProvider
        identifyUser(res.data.id, {
          email: res.data.email,
          name: res.data.full_name,
          onboarding_completed: res.data.onboarding_completed,
        })
        // Silently refresh token on each app launch to extend the 24h window
        // NOTE: /auth/me already succeeded above so user IS authenticated.
        // A refresh failure should NOT log the user out — just keep the current token.
        try {
          const refreshRes = await axios.post(`${API_URL}/api/v1/auth/refresh`, null, { headers, timeout: 5000 })
          if (refreshRes.data?.access_token) {
            await storage.set(TOKEN_KEY, refreshRes.data.access_token)
          }
        } catch (err: any) {
          // Refresh failed — token still valid (validated above), stay logged in
          console.warn('[Auth] Token refresh failed (non-fatal):', err?.response?.status || err?.message)
        }
      } catch {
        await storage.remove(TOKEN_KEY)
        await storage.remove(USER_ID_KEY)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [])

  const loginWithRedirect = useCallback(async (leadId?: string, trackingCode?: string) => {
    try {
      events.loginStarted()
      // Tell backend where to redirect after MitID auth
      const callbackUrl = Platform.OS === 'web'
        ? `${window.location.origin}/mitid/callback`
        : 'https://app.forbrugeragenten.dk/mitid/native-callback'
      const res = await axios.post(`${API_URL}/api/v1/auth/login`, {
        redirect_uri: callbackUrl,
        ...(leadId && { lead_id: leadId }),
        ...(trackingCode && { tracking_code: trackingCode }),
      })
      const { authorization_url } = res.data

      if (Platform.OS === 'web') {
        window.location.href = authorization_url
      } else {
        // Custom scheme — ASWebAuthenticationSession (iOS) extracts the scheme
        // and intercepts any redirect matching it. 'forbrugeragenten://' is registered
        // in app.json. server.js at /mitid/native-callback does the HTTPS→scheme bounce.
        const nativeCallbackUrl = 'forbrugeragenten://'
        const result = await WebBrowser.openAuthSessionAsync(
          authorization_url,
          nativeCallbackUrl
        )

        if (result.type === 'success' && result.url) {
          const url = new URL(result.url)
          const code = url.searchParams.get('code')
          const state = url.searchParams.get('state')

          if (code && state) {
            const callbackRes = await axios.post(`${API_URL}/api/v1/auth/callback`, { code, state }, { timeout: 10000 })
            const { access_token, user_id, is_new_user, onboarding_completed, full_name } = callbackRes.data
            await storage.set(TOKEN_KEY, access_token)
            await storage.set(USER_ID_KEY, user_id)
            await storage.set(HAS_LOGGED_IN_KEY, '1')

            // Set user state immediately from callback data to prevent auth guard race condition.
            // Without this, setUser() from /auth/me could be too late — tabs layout sees
            // isAuthenticated=false and redirects back to login (double MitID prompt).
            setUser({
              id: user_id,
              email: '',
              full_name: full_name || null,
              cpr_number: null,
              mitid_verified: true,
              onboarding_completed: onboarding_completed ?? false,
              fuldmagt_accepted: false,
              consent_given: false,
            })
            identifyUser(user_id, { name: full_name, is_new_user, onboarding_completed })

            // Fetch full profile in background (non-blocking) to get complete user data
            axios.get(`${API_URL}/api/v1/auth/me`, {
              headers: { Authorization: `Bearer ${access_token}` },
              timeout: 8000,
            }).then(meRes => {
              setUser(meRes.data)
            }).catch(() => {}) // Non-fatal — we already have basic user data
          }
        }
        // User cancelled or dismissed — no action needed, just return silently
      }
    } catch (error) {
      console.error('Failed to initiate MitID login:', error)
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    events.logout()
    resetUser()
    clearSentryUser()
    const userId = await storage.get(USER_ID_KEY)
    await clearPushToken(userId || undefined)
    await storage.remove(TOKEN_KEY)
    await storage.remove(USER_ID_KEY)
    // Clear biometric state so another user on same device doesn't unlock previous user's data
    await setBiometricsEnabled(false)
    await storage.remove(BIOMETRIC_PROMPTED_KEY)
    setUser(undefined)
  }, [])

  const getAccessTokenSilently = useCallback(async () => {
    const token = await storage.get(TOKEN_KEY)
    if (!token) {
      throw new Error('Not authenticated')
    }
    return token
  }, [])

  const setUserDirectly = useCallback((u: AuthUser) => {
    setUser(u)
  }, [])

  const refreshUser = useCallback(async () => {
    const token = await storage.get(TOKEN_KEY)
    if (!token) return
    setIsLoading(true) // Suspend auth guards while re-validating (prevents redirect race on login)
    try {
      const res = await axios.get(`${API_URL}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      })
      setUser(res.data)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 401) {
        // Token expired/invalid — clear auth state so user can re-login
        await storage.remove(TOKEN_KEY)
        await storage.remove(USER_ID_KEY)
        setUser(undefined)
      }
      console.warn('refreshUser failed:', status || err?.message)
      // Re-throw non-401 errors so callers can detect stale state
      if (status !== 401) throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      refreshUser,
      setUserDirectly,
    }),
    [user, isLoading, loginWithRedirect, logout, getAccessTokenSilently, refreshUser, setUserDirectly]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper exports — matches Next.js MitIDProvider setToken/clearToken
export async function setToken(token: string) {
  await storage.set(TOKEN_KEY, token)
}

export async function clearToken() {
  await storage.remove(TOKEN_KEY)
}
